import React, {useContext} from 'react';
import {FirebaseContext, SheetContext} from "../../context";
import {AppBar, Button, IconButton, TextField, Toolbar, Typography} from "@mui/material";
import {ExpandMore, ArrowBack} from "@mui/icons-material";
import {Accordion, AccordionDetails, AccordionSummary} from "@mui/material";
import LoadingButton from '@mui/lab/LoadingButton';
import {useFormik} from "formik";
import * as yup from 'yup';
import {RoutingContext} from "../../routing";
import './Settings.scss';
import TabNavBar from "../../components/TabNavBar";

const validationSchema = yup.object({
  spreadsheetId: yup
    .string('Enter your spreadsheet ID')
    .required('Spreadsheet ID is required'),
});

function SettingsScreen({saving}) {
  const firebase = useContext(FirebaseContext);
  const {getSpreadsheet} = useContext(SheetContext);
  const {navigateToProfile} = useContext(RoutingContext);
  const {profile, saveProfile} = firebase;
  const formik = useFormik({
    initialValues: {
      spreadsheetId: profile && profile.spreadsheetId,
      sheetIdExpenses: profile && profile.sheetIdExpenses,
      sheetIdExchanges: profile && profile.sheetIdExchanges,
      sheetIdCategories: profile && profile.sheetIdCategories,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, actions) => {
      actions.setSubmitting(true);
      const profile = {
        ...values,
        spreadsheetId: parseSpreadsheetIdFromUrl(values.spreadsheetId),
      }

      // get sheets from spreadsheet
      const doc = await getSpreadsheet(profile.spreadsheetId);
      if (doc && doc.sheetsByTitle) {
        if (doc.sheetsByTitle['Expenses']) {
          profile.sheetIdExpenses = doc.sheetsByTitle['Expenses'].sheetId;
        }
        if (doc.sheetsByTitle['Exchanges']) {
          profile.sheetIdExchanges = doc.sheetsByTitle['Exchanges'].sheetId;
        }
        if (doc.sheetsByTitle['Categories']) {
          profile.sheetIdCategories = doc.sheetsByTitle['Categories'].sheetId;
        }
      }
      console.log(profile);

      await saveProfile(profile);

      actions.setSubmitting(false);

      navigateToProfile();
    },
  });

  const parseSpreadsheetIdFromUrl = (url) => {
    const regex = /^https:\/\/docs\.google\.com\/spreadsheets\/d\/(.*?)\/edit/gm;

    const match = [...url.matchAll(regex)];
    if (match && match.length && match[0].length > 1) {
      return match[0][1];
    }
    return url;
  }

  return (<>
      <AppBar color="transparent" position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={navigateToProfile}
            aria-label="back"
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Settings
          </Typography>
          <Button></Button>
        </Toolbar>
      </AppBar>

      <div className='screen-content settings-screen'>
        <form onSubmit={formik.handleSubmit}>
          <div className='field-wrapper'>
            <TextField
              fullWidth
              id="spreadsheetId"
              name="spreadsheetId"
              label="Spreadsheet ID"
              value={formik.values.spreadsheetId}
              variant="filled"
              onChange={formik.handleChange}
              error={formik.touched.spreadsheetId && Boolean(formik.errors.spreadsheetId)}
              helperText={formik.touched.spreadsheetId && formik.errors.spreadsheetId}
            />
            <div className="field-desc">
              The spreadsheet ID is the unique identifier of a Google Sheet.
              It is the last part of the spreadsheets URL
              (e.g. https://docs.google.com/spreadsheets/d/[spreadsheet-id]/edit#gid=648206507).
              <br/> <br/>
              Too confusing? No problem. Just paste the whole URL above and we'll figure it out for you!
              <br/><br/>
              You will need to share your Sheet with the email account: <span className="account-email">sheets-connector@expense-log-338212.iam.gserviceaccount.com</span>.
            </div>
          </div>

          <Accordion sx={{boxShadow: 0}}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="advanced-content"
              id="advanced-header"
            >
              <Typography>Advanced Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <div className='field-wrapper'>
                <TextField
                  fullWidth
                  id="sheetIdExpenses"
                  name="sheetIdExpenses"
                  label="Expenses Sheet ID"
                  value={formik.values.sheetIdExpenses}
                  variant="filled"
                  onChange={formik.handleChange}
                  error={formik.touched.sheetIdExpenses && Boolean(formik.errors.sheetIdExpenses)}
                  helperText={formik.touched.sheetIdExpenses && formik.errors.sheetIdExpenses}
                />
              </div>
              <div className='field-wrapper'>
                <TextField
                  fullWidth
                  id="sheetIdExchanges"
                  name="sheetIdExchanges"
                  label="Exchanges Sheet ID"
                  value={formik.values.sheetIdExchanges}
                  variant="filled"
                  onChange={formik.handleChange}
                  error={formik.touched.sheetIdExchanges && Boolean(formik.errors.sheetIdExchanges)}
                  helperText={formik.touched.sheetIdExchanges && formik.errors.sheetIdExchanges}
                />
              </div>
              <div className='field-wrapper'>
                <TextField
                  fullWidth
                  id="sheetIdCategories"
                  name="sheetIdCategories"
                  label="Categories Sheet ID"
                  value={formik.values.sheetIdCategories}
                  variant="filled"
                  onChange={formik.handleChange}
                  error={formik.touched.sheetIdCategories && Boolean(formik.errors.sheetIdCategories)}
                  helperText={formik.touched.sheetIdCategories && formik.errors.sheetIdCategories}
                />
              </div>
            </AccordionDetails>
          </Accordion>

          <LoadingButton loading={saving || formik.isSubmitting} color="primary" variant="contained" fullWidth type="submit">
            Save Changes
          </LoadingButton>
        </form>
      </div>

      <TabNavBar value="profile" />
    </>
  );
}

export default SettingsScreen;
